
// src/services/testService.js

import axios from 'axios';

const API_BASE_URL = 'https://devexam.doode.in/api'; 

/**
 * Checks if the JWT token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if token is expired or invalid
 */
const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = JSON.parse(atob(token.split('.')[1]));
    return decoded.exp < Math.floor(Date.now() / 1000);
  } catch (error) {
    console.error('Error parsing token:', error);
    return true; // Consider invalid tokens as expired
  }
};

/**
 * Handles token expiry by clearing localStorage and redirecting to login
 */
const handleTokenExpiry = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('student');
  localStorage.removeItem('id');
  alert('Session expired. Please log in again.');
  window.location.href = '/student-login';
};

/**
 * Gets authorization headers with JWT token
 * @returns {Object} Headers object with Authorization
 */
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  
  if (isTokenExpired(token)) {
    console.warn("Token expired, redirecting to login...");
    handleTokenExpiry();
    return null;
  }
  
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

/**
 * Generate a test for the student.
 * If the user has exhausted the free tests, the backend will respond with `paymentRequired: true`.
 * @param {string} studentId 
 * @returns {Object} Response data
 */
export const generateTest = async (studentId) => {
  try {
    if (!studentId) {
      throw new Error("Student ID is required");
    }

    console.log("Generating test for studentId:", studentId);

    const headers = getAuthHeaders();
    if (!headers) return; // Token handling was done in getAuthHeaders

    const response = await axios.post(
      `${API_BASE_URL}/test/generate`,
      { studentId },
      { headers }
    );

    console.log("Response received:", response.data);

    if (response.data.paymentRequired) {
      console.warn("Payment required for the test.");
      return {
        exhausted: true,
        message: response.data.message,
      };
    } else {
      return response.data;
    }
  } catch (error) {
    console.error('Error generating test:', error);

    if (error.response && error.response.status === 403) {
      console.error("403 Forbidden - Check if you are logged in or authorized.");
      alert("Session expired or unauthorized access. Please log in again.");
      handleTokenExpiry();
    } else if (error.message === "Student ID is required") {
      alert("Student ID is missing. Please log in and try again.");
      handleTokenExpiry();
    }

    throw new Error(error.response?.data?.message || 'Failed to generate test');
  }
};

/**
 * Complete the test with given results.
 * @param {string} testId 
 * @param {Object} results 
 * @returns {Object} Response data
 */
export const completeTest = async (testId, results) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) return; // Token handling was done in getAuthHeaders

    const response = await axios.post(
      `${API_BASE_URL}/test/complete`, 
      { testId, results },
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error completing test:', error);
    
    if (error.response && error.response.status === 403) {
      handleTokenExpiry();
    }
    
    throw new Error(error.response?.data?.message || 'Failed to complete test');
  }
};

/**
 * Get upcoming tests for the student.
 * @param {string} studentId 
 * @returns {Array} List of upcoming tests
 */
export const getUpcomingTests = async (studentId) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) return; // Token handling was done in getAuthHeaders

    const response = await axios.get(
      `${API_BASE_URL}/test/upcoming/${studentId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching upcoming tests:', error);
    
    if (error.response && error.response.status === 403) {
      handleTokenExpiry();
    }
    
    throw new Error(error.response?.data?.message || 'Failed to fetch upcoming tests');
  }
};

/**
 * Get test details by its ID.
 * @param {string} testId 
 * @returns {Object} Test details
 */
export const getTestById = async (testId) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) return; // Token handling was done in getAuthHeaders

    const response = await axios.get(
      `${API_BASE_URL}/test/${testId}`,
      { headers }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching test by ID:', error);
    
    if (error.response && error.response.status === 403) {
      handleTokenExpiry();
    }
    
    throw new Error(error.response?.data?.message || 'Failed to fetch test by ID');
  }
};

/**
 * Create Razorpay order for payment.
 * @param {string} studentId 
 * @param {number} amount 
 * @returns {Object} Order details from Razorpay
 */
export const createRazorpayOrder = async (studentId, amount) => {
  try {
    // 🔍 Correct URL format with studentId in the path
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/check-status/${studentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { isPaid: false };
      }
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to create Razorpay order');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating Razorpay order:', error);
    throw error;
  }
};



/**
 * Verify Razorpay payment after successful transaction.
 * @param {string} orderId 
 * @param {string} paymentId 
 * @param {string} studentId
 * @returns {Object} Verification response
 */
export const verifyRazorpayPayment = async (orderId, paymentId, studentId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/verify-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        orderId,
        paymentId,
        studentId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Payment verification failed');
    }

    const data = await response.json();
    return {
      success: true,
      ...data
    };
  } catch (error) {
    console.error('Error verifying payment:', error);
    throw error;
  }
};

export const submitTest = async (testId, studentId) => {
  try {
    const headers = getAuthHeaders();
    if (!headers) return;

    const response = await axios.post(
      `${API_BASE_URL}/test/submit`,
      { testId, studentId },
      { headers }
    );

    return response.data;
  } catch (error) {
    console.error('Error submitting test:', error);

    if (error.response && error.response.status === 403) {
      handleTokenExpiry();
    }

    throw new Error(error.response?.data?.message || 'Failed to submit test');
  }
};

/**
 * Check a student's payment status
 * @param {string} studentId - The student's ID
 * @returns {Promise<Object>} - Payment status information
 */
export const checkPaymentStatus = async (studentId) => {
  try {
    const response = await fetch(`${process.env.REACT_APP_API_URL}/api/payment/check-status/${studentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return { isPaid: false };
      }
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to check payment status');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error checking payment status:', error);
    throw error;
  }
};
